/**
 * RTK Query API Slice for Dashboard Data
 * 
 * Handles all dashboard-related endpoints including:
 * - Dashboard data aggregation and filtering
 * - Metrics and templates management
 * - Slicers and filters configuration
 * - Norms and reference data
 * - Batch operations for recordings
 * - Export functionality
 * - Real-time statistics
 * 
 * Key architectural decisions:
 * - Uses existing apiClient as baseQuery (maintains snake_case)
 * - Appropriate caching for medical software compliance
 * - Reference data can be cached (non-PHI)
 * - PHI data has minimal or no caching
 * - Complex data aggregation support
 * - Real-time medical data handling
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: apiClientBaseQuery,
  
  // Configure caching strategy for medical software compliance
  keepUnusedDataFor: 300, // 5 minutes for reference data
  refetchOnMountOrArgChange: true, // Always refetch for data freshness
  refetchOnFocus: false, // Don't refetch on window focus (reduces server load)
  
  // Define cache tags for invalidation
  tagTypes: ['Template', 'Metric', 'Slicer', 'Norm', 'DashboardData', 'DashboardStats'],
  
  endpoints: (builder) => ({
    // Get dashboard data (PHI - minimal caching)
    getDashboardData: builder.query({
      query: (params = {}) => ({
        url: '/v1/dashboard/data',
        method: 'GET',
        params: {
          client_id: params.client_id,
          user_id: params.user_id,
          start_date: params.start_date,
          end_date: params.end_date,
          metric_ids: params.metric_ids,
          template_id: params.template_id,
          ...params,
        },
      }),
      transformResponse: (response) => {
        // Dashboard data includes aggregated metrics
        return response.data || {};
      },
      // PHI data should not be cached for compliance
      keepUnusedDataFor: 0,
      providesTags: (result, error, params) => [
        { type: 'DashboardData', id: JSON.stringify(params) }
      ],
    }),

    // Get dashboard statistics
    getDashboardStatistics: builder.query({
      query: (params = {}) => ({
        url: '/v1/dashboard/statistics',
        method: 'GET',
        params: {
          client_id: params.client_id,
          user_id: params.user_id,
          period: params.period || 'month',
          ...params,
        },
      }),
      transformResponse: (response) => response.data || {},
      // Statistics derived from PHI - no caching for medical compliance
      keepUnusedDataFor: 0, // No caching for medical compliance
      providesTags: ['DashboardStats'],
    }),

    // Get all metrics (reference data - can be cached)
    getAllMetrics: builder.query({
      query: (lightweight = true) => ({
        url: `/v1/get-all-metrics${lightweight ? '?lightweight=true' : ''}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        // Data comes from apiClient already normalized and in snake_case
        return response.data || [];
      },
      providesTags: ['Metric'],
    }),

    // Get metric by type (reference data - can be cached)
    getMetricByType: builder.query({
      query: (slicer_type) => ({
        url: '/v1/get-metric-by-type',
        method: 'GET',
        params: { type: slicer_type, body: '' },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      providesTags: (result, error, slicer_type) => [
        { type: 'Metric', id: slicer_type }
      ],
    }),

    // Get slicers (reference data - can be cached)
    getSlicers: builder.query({
      query: (slicer_type) => ({
        url: `/v1/get-slicers/${slicer_type}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      providesTags: (result, error, slicer_type) => [
        { type: 'Slicer', id: slicer_type }
      ],
    }),

    // Get slicer fields (reference data - can be cached)
    getSlicerFields: builder.query({
      query: () => ({
        url: '/v1/get-slicer-fields',
        method: 'GET',
        params: { field: 'clients.name', body: '' },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      providesTags: ['Slicer'],
    }),

    // Dashboard data aggregation (PHI - no caching)
    aggregateDashboardData: builder.mutation({
      query: (params) => ({
        url: '/v1/dashboard/aggregate',
        method: 'POST',
        data: params, // Complex filtering and aggregation params
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get all templates (reference data - can be cached)
    getAllTemplates: builder.query({
      query: (lightweight = true) => ({
        url: `/v1/get-all-templates${lightweight ? '?lightweight=true' : ''}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      providesTags: ['Template'],
    }),

    // Batch process recordings for dashboard (PHI - no caching)
    batchProcessRecordings: builder.mutation({
      query: (params) => ({
        url: '/v1/dashboard/batch-recordings',
        method: 'POST',
        data: params, // Expects: { recording_ids, metric_ids, options }
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get user metrics configuration
    getUserMetrics: builder.query({
      query: (userId) => ({
        url: '/v1/dashboard/user-metrics',
        method: 'GET',
        params: { user_id: userId },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // User preferences can be cached
      keepUnusedDataFor: 300, // 5 minutes
      providesTags: (result, error, userId) => [
        { type: 'UserMetrics', id: userId }
      ],
    }),

    // Export dashboard data (returns file)
    exportDashboardData: builder.mutation({
      query: (params) => ({
        url: '/v1/dashboard/export-data',
        method: 'POST',
        data: {
          format: params.format || 'csv',
          client_id: params.client_id,
          user_id: params.user_id,
          start_date: params.start_date,
          end_date: params.end_date,
          metric_ids: params.metric_ids,
          include_raw_data: params.include_raw_data || false,
          ...params,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get users by organization (PHI - minimal caching)
    getUsersByOrg: builder.mutation({
      query: (params) => ({
        url: '/v1/get-users-by-org',
        method: 'POST',
        data: {
          org_id: params.org_id,
          lightweight: params.lightweight || false  // Pass lightweight flag for optimized queries
        },
      }),
      transformResponse: (response) => {
        // apiClient normalizes to { data: actualData, metadata, ... }
        // response.data is already the users array
        return response.data || [];
      },
    }),

    // Get all norms (reference data - can be cached)
    getAllNorms: builder.query({
      query: (age) => {
        // Age must be a valid number > 0
        const validAge = age && Number(age) > 0 ? Number(age) : null;
        if (!validAge) {
          throw new Error('Age parameter is required for norms query');
        }
        return {
          url: `/v1/get-all-norms?age=${validAge}`,
          method: 'GET',
        };
      },
      transformResponse: (response) => {
        // console.log('Norms API Raw Response:', response);
        const data = response.data || {};
        // console.log('Norms API Transformed Data:', data);
        return data;
      },
      providesTags: ['Norm'],
    }),

    // Get filter recordings statistics (PHI - minimal caching)
    getFilterRecordingsStats: builder.mutation({
      query: (params) => ({
        url: '/v1/get-filter-recordings-stats',
        method: 'POST',
        data: params, // Complex filter parameters
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get dashboard configuration
    getDashboardConfig: builder.query({
      query: (params = {}) => ({
        url: '/v1/dashboard/config',
        method: 'GET',
        params: {
          client_id: params.client_id,
          user_id: params.user_id,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // Config can be cached
      keepUnusedDataFor: 300, // 5 minutes
      providesTags: (result, error, params) => [
        { type: 'DashboardConfig', id: JSON.stringify(params) }
      ],
    }),

    // Update dashboard configuration
    updateDashboardConfig: builder.mutation({
      query: (configData) => ({
        url: '/v1/dashboard/config',
        method: 'PUT',
        data: configData, // Expects: { user_id, client_id, config }
      }),
      invalidatesTags: (result, error, { user_id, client_id }) => [
        { type: 'DashboardConfig', id: JSON.stringify({ user_id, client_id }) }
      ],
    }),

    // Get metric trends (PHI - minimal caching)
    getMetricTrends: builder.query({
      query: (params) => ({
        url: '/v1/dashboard/metric-trends',
        method: 'GET',
        params: {
          metric_id: params.metric_id,
          user_id: params.user_id,
          period: params.period || '30d',
          granularity: params.granularity || 'daily',
          ...params,
        },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      // Trends derived from PHI - no caching for medical compliance
      keepUnusedDataFor: 0, // No caching for medical compliance
      providesTags: (result, error, params) => [
        { type: 'MetricTrends', id: JSON.stringify(params) }
      ],
    }),

    // Get comparative analysis
    getComparativeAnalysis: builder.query({
      query: (params) => ({
        url: '/v1/dashboard/comparative-analysis',
        method: 'GET',
        params: {
          user_ids: params.user_ids,
          metric_ids: params.metric_ids,
          start_date: params.start_date,
          end_date: params.end_date,
          group_by: params.group_by || 'user',
          ...params,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // PHI data - no caching
      keepUnusedDataFor: 0,
    }),

    // Get dashboard alerts/notifications
    getDashboardAlerts: builder.query({
      query: (params = {}) => ({
        url: '/v1/dashboard/alerts',
        method: 'GET',
        params: {
          user_id: params.user_id,
          client_id: params.client_id,
          unread_only: params.unread_only || false,
          limit: params.limit || 50,
        },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      // Alerts should be real-time
      keepUnusedDataFor: 0,
      providesTags: ['DashboardAlerts'],
    }),

    // Mark alert as read
    markAlertAsRead: builder.mutation({
      query: (alertId) => ({
        url: `/v1/dashboard/alerts/${alertId}/read`,
        method: 'PUT',
      }),
      invalidatesTags: ['DashboardAlerts'],
    }),

    // Get dashboard widgets configuration
    getDashboardWidgets: builder.query({
      query: (userId) => ({
        url: '/v1/dashboard/widgets',
        method: 'GET',
        params: { user_id: userId },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      // Widget config can be cached
      keepUnusedDataFor: 300, // 5 minutes
      providesTags: (result, error, userId) => [
        { type: 'DashboardWidgets', id: userId }
      ],
    }),

    // Update dashboard widgets
    updateDashboardWidgets: builder.mutation({
      query: (widgetData) => ({
        url: '/v1/dashboard/widgets',
        method: 'PUT',
        data: widgetData, // Expects: { user_id, widgets: [...] }
      }),
      invalidatesTags: (result, error, { user_id }) => [
        { type: 'DashboardWidgets', id: user_id }
      ],
    }),

    // Get real-time dashboard updates (WebSocket fallback)
    getDashboardUpdates: builder.query({
      query: (params) => ({
        url: '/v1/dashboard/updates',
        method: 'GET',
        params: {
          user_id: params.user_id,
          since: params.since || new Date().toISOString(),
        },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      // Real-time data - no caching
      keepUnusedDataFor: 0,
    }),

    // Search functionality for dashboard
    searchDashboard: builder.query({
      query: (params) => ({
        url: '/v1/dashboard/search',
        method: 'GET',
        params: {
          q: params.query,
          type: params.type || 'all', // users, recordings, metrics
          client_id: params.client_id,
          limit: params.limit || 20,
        },
      }),
      transformResponse: (response) => {
        return response.data || { users: [], recordings: [], metrics: [] };
      },
      // Search results cached briefly
      keepUnusedDataFor: 30, // 30 seconds
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetDashboardDataQuery,
  useGetDashboardStatisticsQuery,
  useGetAllMetricsQuery,
  useGetMetricByTypeQuery,
  useGetSlicersQuery,
  useGetSlicerFieldsQuery,
  useGetAllTemplatesQuery,
  useGetUserMetricsQuery,
  useGetAllNormsQuery,
  useGetDashboardConfigQuery,
  useGetMetricTrendsQuery,
  useGetComparativeAnalysisQuery,
  useGetDashboardAlertsQuery,
  useGetDashboardWidgetsQuery,
  useGetDashboardUpdatesQuery,
  useSearchDashboardQuery,
  
  // Mutations
  useAggregateDashboardDataMutation,
  useBatchProcessRecordingsMutation,
  useExportDashboardDataMutation,
  useGetUsersByOrgMutation,
  useGetFilterRecordingsStatsMutation,
  useUpdateDashboardConfigMutation,
  useMarkAlertAsReadMutation,
  useUpdateDashboardWidgetsMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetDashboardDataQuery,
  useLazyGetMetricTrendsQuery,
  useLazySearchDashboardQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = dashboardApi;

export default dashboardApi;